import { useState } from 'react';

import successIcon from '@/assets/images/success-icon.webp';
import warningIcon from '@/assets/images/warning-icon.webp';
import { ResultDialog } from '@/components/feedback/result-dialog';
import type { Device } from '@/features/devices';
import { FeedbackPrompt } from '@/features/feedback';

import { MAX_OPEN_TICKETS } from '../constants';
import { useOpenTicketCount } from '../hooks';

import { RaiseSupportTicketModal } from './raise-support-ticket-modal';

export type RaiseSupportTicketFlowProps = {
  open: boolean;
  /** Fixed device (device-detail path). Omit for the profile path. */
  device?: Device;
  /** Selectable devices (profile path). */
  devices?: Device[];
  onClose: () => void;
};

type Override = 'success' | 'feedback' | 'limit' | 'duplicate' | null;

/**
 * Orchestrates the Raise-Support-Ticket experience from the device-detail context:
 *
 *   open → [open-ticket-limit gate] → form → success → feedback prompt
 *
 * On open the grower's Open/Active count is checked; at the limit (AC §7) the form is
 * skipped for the limit dialog. A successful submit shows the success dialog, which leads
 * into the feedback prompt. A duplicate submission (AC §8) surfaces the duplicate dialog
 * (Figma 2614-380843). The limit is also caught defensively from the submit response.
 */
export function RaiseSupportTicketFlow({
  open,
  device,
  devices = [],
  onClose,
}: RaiseSupportTicketFlowProps) {
  const countQuery = useOpenTicketCount(open);
  const [override, setOverride] = useState<Override>(null);

  // Reset the step machine each time the flow (re)opens.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOverride(null);
  }

  const atLimit = countQuery.isSuccess && (countQuery.data ?? 0) >= MAX_OPEN_TICKETS;
  const showLimit = open && (override === 'limit' || (override === null && atLimit));
  const showForm = open && override === null && !atLimit;

  return (
    <>
      <RaiseSupportTicketModal
        open={showForm}
        device={device}
        devices={devices}
        onClose={onClose}
        onSubmitted={() => setOverride('success')}
        onLimitReached={() => setOverride('limit')}
        onDuplicate={() => setOverride('duplicate')}
      />

      <ResultDialog
        open={open && override === 'success'}
        icon={successIcon}
        title="Success"
        subtitle="Your support ticket has been submitted successfully. Our team will get back to you soon."
        onClose={() => setOverride('feedback')}
      />

      <ResultDialog
        open={showLimit}
        icon={warningIcon}
        title="You have reached the open ticket limit"
        subtitle="Please wait for existing tickets to be resolved."
        onClose={onClose}
      />

      <ResultDialog
        open={open && override === 'duplicate'}
        icon={warningIcon}
        title="Looks like you just raised this ticket"
        subtitle="Please check your existing tickets."
        onClose={onClose}
      />

      <FeedbackPrompt open={open && override === 'feedback'} onClose={onClose} />
    </>
  );
}
